import { sessionStorage } from 'dibs-browser-storage';
import SV from 'server-vars';
import isPageType from '../utils/isPageType';
import globalNotifications from './globalNotifications';
import {
    KEY_DISABLE_NOTIFICATIONS,
    KEY_ISOLATE_NOTIFICATION,
    KEY_FORCE_NOTIFICATION,
} from './notificationQueueConstants';

import { shouldSkipNotifForTrade, incrementTradeNotifStorage } from './tradeHelpers';
import { MOBILE, TABLET, DESKTOP } from '../../exports/deviceTypeEnums';

const NO_OP_ACTION_TYPE = { actionType: 'noop' };

let hasBeenInitialized = false;
let queue;

function createStackTrace(msg) {
    const err = new Error(msg);
    if (typeof Error.captureStackTrace === 'function') {
        Error.captureStackTrace(err, createStackTrace);
    }
    if (err.stack) {
        return err.stack;
    }
    return err;
}

function checkRuleForConfigErrors(ruleConfig) {
    const { allowedPageTypes = [], blockedPageTypes = [] } = ruleConfig;
    if (allowedPageTypes.length && blockedPageTypes.length) {
        throw new Error(
            `NOTIFICATION QUEUE > Both "allowedPageTypes" and "blockedPageTypes" can't be present within one rule. Please consider updating "${ruleConfig.name}" rule config!`
        );
    }
}

/**
 * Filter notification queue rules by whitelisted or blacklisted pages
 * NOTE, that both whitelisted and blacklisted page types can't be present,
 * only whitelisted page types will be considered in the filter.
 */
function ruleConfigsFilter(ruleConfig) {
    const { deviceTypes = [], allowedPageTypes = [], blockedPageTypes = [] } = ruleConfig;
    let pageTypeAllowed = true;

    checkRuleForConfigErrors(ruleConfig);

    if (allowedPageTypes.length) {
        pageTypeAllowed = isPageType(allowedPageTypes);
    } else if (blockedPageTypes.length) {
        pageTypeAllowed = !isPageType(blockedPageTypes);
    }

    // If no device types were passed in config - all devices suits
    const deviceAllowed =
        deviceTypes.length === 0 ||
        deviceTypes.filter(deviceType => {
            switch (deviceType) {
                case MOBILE:
                    return SV.get('settings.isMobile') || false;
                case TABLET:
                    return SV.get('settings.isTablet') || false;
                case DESKTOP:
                    return SV.get('settings.isDesktop') || false;
            }
            return false;
        }).length;

    return pageTypeAllowed && deviceAllowed;
}

export default class Queue {
    constructor(notifications, store, relayEnvironment, autoRun = true, customActionTypes = {}) {
        this.store = store;
        this.relayEnvironment = relayEnvironment;
        // allows a way for the consumer to hook into the global
        // notification queue when its action has been executed
        this.customActionTypes = customActionTypes;
        // should onl be used for testing purpsose
        // see comment for `KEY_FORCE_NOTIFICATION`
        this.forceNotification = false;

        if (!hasBeenInitialized) {
            queue = this.buildQueue(notifications);
            hasBeenInitialized = true;

            if (autoRun) {
                this.runQueue();
            }
        } else {
            throw new Error('Only one notification queue may be initialized');
        }
    }

    buildQueue(notifications) {
        const query = new URLSearchParams(window.location.search);
        const isolatedNotification = query.get(KEY_ISOLATE_NOTIFICATION);

        const fullQueue = []
            .concat(globalNotifications, notifications)
            .filter(ruleConfigsFilter)
            .sort((a, b) => {
                return b.priority - a.priority;
            });
        if (isolatedNotification) {
            const isolatedNotificationConfig = fullQueue.find(
                item => item.name === isolatedNotification
            );
            if (!isolatedNotificationConfig) {
                // eslint-disable-next-line no-console
                console.warn(`${isolatedNotification} not found in queue`);
                return [];
            }
            const forceNotification = query.get(KEY_FORCE_NOTIFICATION);
            if (forceNotification === 'true') {
                this.forceNotification = true;
            }
            return [isolatedNotificationConfig];
        }
        if (this.noNotifications(query)) {
            return [];
        }
        if (shouldSkipNotifForTrade()) {
            return [];
        }

        return fullQueue;
    }

    noNotifications(query) {
        if (sessionStorage.getItem(KEY_DISABLE_NOTIFICATIONS) === true) {
            return true;
        }
        if (query.has(KEY_DISABLE_NOTIFICATIONS)) {
            sessionStorage.setItem(KEY_DISABLE_NOTIFICATIONS, true);
            return true;
        }

        return false;
    }

    _getNotificationQueue() {
        // eslint-disable-next-line no-console
        console.error('this method is for development purposes only');
        return queue;
    }

    async runQueue(index = 0) {
        if (index < queue.length) {
            const notification = queue[index];
            const showNotification = await notification.rule({
                relayEnvironment: this.relayEnvironment,
                forceNotification: this.forceNotification,
                store: this.store,
            });
            if (showNotification) {
                const notificationName = notification.name;
                const customActionType =
                    this.customActionTypes && this.customActionTypes[notificationName];
                // object if we want to potentially expand on this concept
                const notifArgs = {
                    customActionType: customActionType || NO_OP_ACTION_TYPE,
                    environment: this.relayEnvironment,
                    runNextNotification: () => this.runQueue(++index),
                };
                if (
                    customActionType &&
                    window.newrelic &&
                    typeof window.newrelic.addPageAction === 'function'
                ) {
                    window.newrelic.addPageAction('dbl:audit', {
                        notificationName,
                        customActionType,
                        stack: createStackTrace(`${notificationName} called`),
                    });
                }
                incrementTradeNotifStorage();
                return this.store.dispatch(notification.action(notifArgs));
            } else {
                return await this.runQueue(++index);
            }
        } else {
            return Promise.resolve();
        }
    }
}

export const _testReset = () => {
    hasBeenInitialized = false;
};
